import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'

/* ---------- Shared building blocks ---------- */
const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.6, delay },
})

const Accent = ({ children }) => (
  <span className="bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 bg-clip-text text-transparent">
    {children}
  </span>
)

const focusRing = 'focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-500/40'

const bars = ['bg-orange-500', 'bg-red-500', 'bg-amber-500', 'bg-rose-500']
const hovers = [
  'hover:border-orange-300 hover:shadow-orange-100',
  'hover:border-red-300 hover:shadow-red-100',
  'hover:border-amber-300 hover:shadow-amber-100',
  'hover:border-rose-300 hover:shadow-rose-100',
]

const Band = ({ tint = false, children }) => (
  <section className={`relative overflow-hidden py-20 md:py-28 ${tint ? 'bg-stone-50' : 'bg-white'}`}>
    <div className="relative max-w-6xl mx-auto px-6">{children}</div>
  </section>
)

const SectionHead = ({ title, text }) => (
  <motion.div {...reveal()} className="mb-14 md:mb-16 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 items-end">
    <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900">{title}</h2>
    {text && <p className="text-lg md:text-xl leading-relaxed text-slate-600">{text}</p>}
  </motion.div>
)

/* ---------- Hero ---------- */
const PricingHero = ({ title, subtitle }) => (
  <section className="relative overflow-hidden bg-slate-900 text-white">
    <div
      aria-hidden="true"
      className="absolute inset-0"
      style={{
        backgroundImage:
          'radial-gradient(55rem 26rem at 90% -10%, rgba(249,115,22,.45), transparent 60%), radial-gradient(40rem 22rem at -5% 110%, rgba(244,63,94,.25), transparent 60%)',
      }}
    />
    <svg aria-hidden="true" className="absolute bottom-8 left-0 w-full h-28 text-[#F97316]/50" viewBox="0 0 1200 112" preserveAspectRatio="none" fill="none">
      <path d="M0 70 C 200 10, 400 120, 600 62 S 1000 10, 1200 70" stroke="currentColor" strokeWidth="2.5" strokeDasharray="10 9" strokeLinecap="round" />
    </svg>
    <div className="relative max-w-6xl mx-auto px-6 py-28 md:py-36">
      <motion.h1
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.02] max-w-4xl"
      >
        {title}
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.15 }}
        className="mt-7 text-lg md:text-2xl text-slate-300 max-w-2xl leading-relaxed"
      >
        {subtitle}
      </motion.p>
    </div>
  </section>
)

/* ---------- "What is ...?" split ---------- */
const IntroSplit = ({ title, text, checks, image, alt }) => (
  <Band>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
      <motion.div {...reveal()}>
        <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05] mb-6">{title}</h2>
        <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-lg">{text}</p>
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {checks.map((c) => (
            <li key={c} className="flex items-center gap-3 text-slate-700 font-medium">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F97316]/10 text-[#F97316] text-sm">✓</span>
              {c}
            </li>
          ))}
        </ul>
      </motion.div>

      <motion.div {...reveal(0.1)} className="relative text-center">
        <div aria-hidden="true" className="absolute -inset-3 rounded-[2rem] border-2 border-dashed border-[#F97316]/50" />
        <img src={image} alt={alt} className="relative rounded-3xl shadow-xl mx-auto" />
      </motion.div>
    </div>
  </Band>
)

/* ---------- Generic color-strip card grid ---------- */
const CardGrid = ({ title, sub, items, tint = false }) => (
  <Band tint={tint}>
    <SectionHead title={title} text={sub} />
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-5 ${items.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
      {items.map((item, i) => {
        const name = item.title || item.name
        return (
          <motion.div key={name + i} {...reveal(i * 0.1)} className="h-full">
            <div className={`group relative h-full overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 pt-10 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${hovers[i % 4]}`}>
              <div className={`absolute left-0 top-0 h-1.5 w-full ${bars[i % 4]}`} />
              <h3 className="text-2xl font-bold tracking-tight mb-3">{name}</h3>
              <div className="mb-4 border-t border-dashed border-slate-300" />
              <p className="text-slate-600 leading-relaxed">{item.description}</p>
            </div>
          </motion.div>
        )
      })}
    </div>
  </Band>
)

/* ---------- Pricing plans + add-ons ---------- */
const Plans = ({ heading, sub, plans, addons, featured = 1, tint = false }) => {
  const [open, setOpen] = useState(null)
  const toggle = (i) => setOpen(open === i ? null : i)

  return (
    <Band tint={tint}>
      <SectionHead title={heading} text={sub} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch pt-4">
        {plans.map((plan, index) => {
          const f = index === featured
          return (
            <div key={plan.title} className={`h-full ${f ? 'lg:-translate-y-4' : ''}`}>
              <motion.div
                {...reveal(index * 0.1)}
                className={`relative flex h-full flex-col rounded-3xl p-8 ${
                  f ? 'bg-slate-900 text-white shadow-2xl' : 'bg-white border border-slate-200 shadow-sm hover:shadow-lg transition-shadow'
                }`}
              >
                {f && (
                  <div className="absolute -top-3.5 left-8 rounded-full bg-[#F97316] px-4 py-1 text-sm font-semibold text-white shadow-md">
                    Most Popular
                  </div>
                )}

                <h3 className="text-xl font-bold leading-snug mb-4 min-h-[3.5rem]">{plan.title}</h3>
                <div className="text-4xl font-extrabold tracking-tight text-[#F97316] mb-3">{plan.price}</div>
                <p className={`mb-6 ${f ? 'text-slate-300' : 'text-slate-600'}`}>{plan.description}</p>

                <div className={`border-t border-dashed mb-6 ${f ? 'border-slate-600' : 'border-slate-300'}`} />

                <ul className="space-y-3.5 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F97316] text-[11px] text-white">✓</span>
                      <span className={f ? 'text-slate-200' : 'text-slate-700'}>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  <div className="mb-6">
                    <button
                      onClick={() => toggle(index)}
                      aria-expanded={open === index}
                      className={`flex items-center justify-between gap-3 w-full rounded-xl px-4 py-3 text-left text-sm transition-colors ${focusRing} ${
                        f ? 'bg-white/10 text-slate-200 hover:bg-white/15' : 'bg-stone-100 text-slate-600 hover:bg-stone-200'
                      }`}
                    >
                      <span><strong>Best for:</strong> {plan.bestFor}</span>
                      <svg className={`w-4 h-4 shrink-0 transition-transform ${open === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    <AnimatePresence initial={false}>
                      {open === index && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className={`mt-3 rounded-xl border border-dashed p-4 ${f ? 'border-slate-600' : 'border-slate-300'}`}>
                            <p className={`text-sm ${f ? 'text-slate-200' : 'text-slate-700'}`}>{plan.bestFor}</p>
                            <p className={`text-xs mt-2 ${f ? 'text-slate-400' : 'text-slate-500'}`}>
                              This plan is ideal for {plan.bestFor.toLowerCase()} and includes all the features listed above.
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <Link
                    to="/contact"
                    className={`block w-full rounded-xl text-center py-3.5 font-semibold text-white transition-colors ${focusRing} ${
                      f ? 'bg-[#F97316] hover:bg-[#EA580C]' : 'bg-slate-900 hover:bg-[#F97316]'
                    }`}
                  >
                    Get Started
                  </Link>
                </div>
              </motion.div>
            </div>
          )
        })}
      </div>

      {addons && (
        <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {addons.map((item, i) => (
            <motion.div key={item.service} {...reveal(i * 0.08)} className="rounded-2xl border border-dashed border-slate-300 bg-white p-6">
              <div className="text-2xl font-bold text-[#F97316] mb-1">{item.price}</div>
              <h3 className="font-semibold mb-1">{item.service}</h3>
              <p className="text-sm text-slate-600">{item.description}</p>
            </motion.div>
          ))}
        </div>
      )}
    </Band>
  )
}

/* ---------- Process steps ---------- */
const Process = ({ steps, tint = false }) => (
  <Band tint={tint}>
    <SectionHead title={<>Our <Accent>Process</Accent></>} />
    <div className="relative grid grid-cols-1 md:grid-cols-4 gap-10">
      <div aria-hidden="true" className="hidden md:block absolute top-8 left-[12%] right-[12%] border-t-2 border-dashed border-[#F97316]/50" />
      {steps.map((item, i) => (
        <motion.div key={item.step} {...reveal(i * 0.1)} className="relative text-center">
          <div className={`relative mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#F97316] text-2xl font-bold text-white ring-8 ${tint ? 'ring-stone-50' : 'ring-white'}`}>
            {item.step}
          </div>
          <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
          <p className="text-slate-600">{item.description}</p>
        </motion.div>
      ))}
    </div>
  </Band>
)

/* ---------- Call to action ---------- */
const CTA = ({ title, text }) => (
  <section className="bg-white px-6 py-20 md:py-28">
    <motion.div {...reveal()} className="relative max-w-6xl mx-auto overflow-hidden rounded-[2rem] bg-slate-900 px-8 py-16 md:py-24 text-center text-white">
      <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-16 h-72 w-72 rounded-full bg-orange-500/40 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-3 rounded-[1.5rem] border-2 border-dashed border-orange-400/30" />
      <div className="relative">
        <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-5">{title}</h2>
        <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-xl mx-auto leading-relaxed">{text}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/contact" className={`rounded-xl bg-[#F97316] px-8 py-3.5 text-lg font-semibold text-white transition-colors hover:bg-[#EA580C] ${focusRing}`}>
            Get Started Now
          </Link>
          <Link to="/portfolio" className={`rounded-xl border-2 border-white/70 px-8 py-3.5 text-lg font-semibold text-white transition-colors hover:bg-white hover:text-slate-900 ${focusRing}`}>
            View Samples
          </Link>
        </div>
      </div>
    </motion.div>
  </section>
)

/* ---------- Page ---------- */
const patchTypes = [
  { name: "Embroidered Patches", image: "/portfolio-snapshot/patches/p11.jpg" },
  { name: "Chenille Patches", image: "/portfolio-snapshot/patches/p3.jpg" },
  { name: "Chenille Patches", image: "/portfolio-snapshot/patches/p4.jpg" },
  { name: "Leather Patches", image: "/portfolio-snapshot/patches/before5.jpg" }
]

const features = [
  { title: "Custom Design", description: "Unique patches designed specifically for your brand and requirements" },
  { title: "Quality Materials", description: "Premium materials ensuring durability and professional appearance" },
  { title: "Fast Turnaround", description: "Quick production and delivery to meet your timeline" }
]

const steps = [
  { step: "1", title: "Design", description: "Create your custom patch design" },
  { step: "2", title: "Review", description: "We review and optimize the design" },
  { step: "3", title: "Production", description: "Manufacture your patches" },
  { step: "4", title: "Deliver", description: "Ship your patches worldwide" }
]

const useCases = [
  { title: "Corporate Branding", description: "Company logos and brand identity patches" },
  { title: "Uniform Identification", description: "Employee name and role patches" },
  { title: "Military & Law Enforcement", description: "Professional identification patches" }
]

const PatchesPricingPage = () => {
  return (
    <div className="pt-20 min-h-screen bg-white text-slate-900 antialiased">
      <PricingHero
        title={<>Custom Patches <span className="text-[#F97316]">Pricing</span></>}
        subtitle="Unique patch designs for branding and identification with competitive pricing"
      />

      {/* Patches Pricing Section */}
      <Band>
        <motion.div
          {...reveal()}
          className="relative mx-auto max-w-4xl overflow-hidden rounded-[2rem] border-2 border-dashed border-orange-300 bg-orange-50/60 px-8 py-14 md:py-16 text-center"
        >
          <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-16 h-56 w-56 rounded-full bg-orange-300/40 blur-3xl" />
          <div className="relative">
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6">
              Patches <Accent>(On Quote)</Accent>
            </h2>
            <p className="text-lg md:text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Custom patch pricing varies based on design complexity, materials, and quantity.
              Contact us for a personalized quote tailored to your specific needs.
            </p>
            <Link
              to="/contact"
              className="inline-block rounded-xl bg-slate-900 px-12 py-4 text-xl font-semibold text-white shadow-lg transition-colors hover:bg-[#F97316] focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-500/40"
            >
              Request a Quote
            </Link>
          </div>
        </motion.div>

        {/* Patch Types Grid */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-8">
          {patchTypes.map((type, index) => (
            <motion.div key={index} {...reveal((index % 2) * 0.1)} className="h-full">
              <div className="group relative h-full overflow-hidden rounded-3xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-orange-300 hover:shadow-2xl hover:shadow-orange-100">
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src={type.image}
                    alt={type.name}
                    className="w-full h-80 object-cover rounded-lg shadow-md transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      console.error(`Failed to load image: ${type.image}`)
                      e.target.style.display = 'none'
                    }}
                  />
                  {type.name && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2 text-center backdrop-blur-sm">
                      <h3 className="font-semibold">{type.name}</h3>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Band>

      <CardGrid
        tint
        title={<>Why Choose <Accent>Our Patches</Accent>?</>}
        sub="Professional quality patches with custom designs tailored to your needs"
        items={features}
      />

      <Process steps={steps} />

      <CardGrid
        tint
        title={<>Perfect for <Accent>Every Business</Accent></>}
        sub="Custom patches are ideal for various industries and applications"
        items={useCases}
      />

      <CTA
        title={<>Ready to Create <span className="text-[#F97316]">Custom Patches</span>?</>}
        text="Get started with our professional custom patch services today"
      />
    </div>
  )
}

export default PatchesPricingPage